import { useReducer, useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { generateClient } from 'aws-amplify/api';
import { useAuth } from '../providers/AuthProvider';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { ErrorMessage } from '../components/ErrorMessage';
import { IngredientForm } from '../components/ingredients/IngredientForm';
import { validateIngredientForm } from '../utils/ingredientValidation';
import { getIngredient, createIngredient, updateIngredient } from '../graphql/ingredients';
import type { IngredientFormData, IngredientValidationError, Varietal } from '../types/ingredient';

const client = generateClient();

// --- State and Reducer ---

const initialFormData: IngredientFormData = {
  name: '',
  brand: '',
  strain: '',
  varietal: '',
  thc: '',
  thca: '',
  cbd: '',
  cbda: '',
  cbg: '',
  cbn: '',
};

type IngredientFormAction =
  | { type: 'SET_FIELD'; field: keyof IngredientFormData; value: string }
  | { type: 'LOAD_INGREDIENT'; data: IngredientFormData };

function ingredientFormReducer(
  state: IngredientFormData,
  action: IngredientFormAction
): IngredientFormData {
  switch (action.type) {
    case 'SET_FIELD':
      return { ...state, [action.field]: action.value };
    case 'LOAD_INGREDIENT':
      return action.data;
    default:
      return state;
  }
}

// --- GraphQL response types ---

interface IngredientGqlResponse {
  userId: string;
  ingredientId: string;
  name: string;
  brand?: string | null;
  strain?: string | null;
  varietal?: string | null;
  thc?: number | null;
  thca?: number | null;
  cbd?: number | null;
  cbda?: number | null;
  cbg?: number | null;
  cbn?: number | null;
  createdAt: string;
  updatedAt: string;
}

// --- Component ---

export function IngredientFormPage() {
  const { ingredientId } = useParams<{ ingredientId?: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isEditMode = !!ingredientId;

  const [formData, dispatch] = useReducer(ingredientFormReducer, initialFormData);
  const [validationErrors, setValidationErrors] = useState<IngredientValidationError[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [notFound, setNotFound] = useState(false);

  // Fetch ingredient data in edit mode
  useEffect(() => {
    if (!isEditMode || !user) return;

    async function fetchIngredient() {
      setIsLoading(true);
      setLoadError(null);
      setNotFound(false);
      try {
        const response = await client.graphql({
          query: getIngredient,
          variables: { userId: user!.userId, ingredientId },
        });
        const ingredient = (response as { data: { getIngredient: IngredientGqlResponse | null } })
          .data.getIngredient;
        if (!ingredient) {
          setNotFound(true);
        } else {
          dispatch({
            type: 'LOAD_INGREDIENT',
            data: {
              name: ingredient.name || '',
              brand: ingredient.brand || '',
              strain: ingredient.strain || '',
              varietal: (ingredient.varietal as Varietal) || '',
              thc: ingredient.thc != null ? String(ingredient.thc) : '',
              thca: ingredient.thca != null ? String(ingredient.thca) : '',
              cbd: ingredient.cbd != null ? String(ingredient.cbd) : '',
              cbda: ingredient.cbda != null ? String(ingredient.cbda) : '',
              cbg: ingredient.cbg != null ? String(ingredient.cbg) : '',
              cbn: ingredient.cbn != null ? String(ingredient.cbn) : '',
            },
          });
        }
      } catch {
        setLoadError('Failed to load ingredient. Please try again.');
      } finally {
        setIsLoading(false);
      }
    }

    fetchIngredient();
  }, [isEditMode, ingredientId, user]);

  // Pre-populate form from navigation state (e.g., fallback from import page with partial data)
  useEffect(() => {
    if (isEditMode) return;
    const prePopulatedData = (location.state as { prePopulatedData?: IngredientFormData } | null)?.prePopulatedData;
    if (prePopulatedData) {
      dispatch({ type: 'LOAD_INGREDIENT', data: prePopulatedData });
    }
  }, [isEditMode, location.state]);

  // Handlers
  function handleFieldChange(field: keyof IngredientFormData, value: string) {
    dispatch({ type: 'SET_FIELD', field, value });
    // Clear validation error for this field (Requirement 7.7)
    setValidationErrors((prev) => prev.filter((e) => e.field !== field));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitError(null);

    // Run validation
    const errors = validateIngredientForm(formData);
    setValidationErrors(errors);

    if (errors.length > 0) {
      return;
    }

    // Parse cannabinoid strings to numbers (undefined for empty strings)
    const parseCannabinoid = (val: string): number | undefined => {
      if (val === '') return undefined;
      return parseFloat(val);
    };

    setIsSubmitting(true);

    try {
      if (isEditMode) {
        await client.graphql({
          query: updateIngredient,
          variables: {
            input: {
              userId: user!.userId,
              ingredientId: ingredientId!,
              name: formData.name.trim(),
              brand: formData.brand || undefined,
              strain: formData.strain || undefined,
              varietal: formData.varietal || undefined,
              thc: parseCannabinoid(formData.thc),
              thca: parseCannabinoid(formData.thca),
              cbd: parseCannabinoid(formData.cbd),
              cbda: parseCannabinoid(formData.cbda),
              cbg: parseCannabinoid(formData.cbg),
              cbn: parseCannabinoid(formData.cbn),
              updatedAt: new Date().toISOString(),
            },
          },
        });
        navigate(`/ingredients/${ingredientId}`);
      } else {
        const newIngredientId = crypto.randomUUID();
        const now = new Date().toISOString();

        await client.graphql({
          query: createIngredient,
          variables: {
            input: {
              userId: user!.userId,
              ingredientId: newIngredientId,
              name: formData.name.trim(),
              brand: formData.brand || undefined,
              strain: formData.strain || undefined,
              varietal: formData.varietal || undefined,
              thc: parseCannabinoid(formData.thc),
              thca: parseCannabinoid(formData.thca),
              cbd: parseCannabinoid(formData.cbd),
              cbda: parseCannabinoid(formData.cbda),
              cbg: parseCannabinoid(formData.cbg),
              cbn: parseCannabinoid(formData.cbn),
              createdAt: now,
              updatedAt: now,
            },
          },
        });
        navigate('/ingredients');
      }
    } catch {
      setSubmitError(
        isEditMode
          ? 'Failed to update ingredient. Please try again.'
          : 'Failed to save ingredient. Please try again.'
      );
    } finally {
      setIsSubmitting(false);
    }
  }

  // Render
  if (isLoading) {
    return (
      <div className="page ingredient-form-page">
        <LoadingSpinner />
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="page ingredient-form-page">
        <ErrorMessage message="Ingredient not found." />
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="page ingredient-form-page">
        <ErrorMessage message={loadError} />
      </div>
    );
  }

  return (
    <div className="page ingredient-form-page">
      <h2>{isEditMode ? 'Edit Ingredient' : 'Create Ingredient'}</h2>

      {submitError && <ErrorMessage message={submitError} />}

      <IngredientForm
        formData={formData}
        validationErrors={validationErrors}
        onFieldChange={handleFieldChange}
        onSubmit={handleSubmit}
        isSubmitting={isSubmitting}
        isEditMode={isEditMode}
      />
    </div>
  );
}
